import React, { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { usePricingFetch, fetchNextPage } from '../../hooks/usePricingFetch';
import { regions, formatPrice } from '../../config/pricingConfig';

const PublicIPPricing = () => {
  const [region, setRegion] = useState('');
  const [sku, setSku] = useState('');
  const [searchParams, setSearchParams] = useState(null);
  const [extraItems, setExtraItems] = useState([]);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sortKey, setSortKey] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);

  const filter = searchParams
    ? `armRegionName eq '${searchParams.region}' and productName eq 'IP Addresses' and serviceName eq 'Virtual Network' and type eq 'Consumption' and contains(meterName, 'Static') eq true and skuName eq '${searchParams.sku}'`
    : '';

  const { items, isLoading, error, nextPageLink } = usePricingFetch({
    queryKey: `publicip-${filter}`,
    filter,
    enabled: !!searchParams,
  });

  const handleFetch = () => {
    setSearchParams({ region, sku });
    setExtraItems([]);
    setNextPageUrl(null);
  };

  const handleLoadMore = async () => {
    const url = nextPageUrl || nextPageLink;
    if (!url) return;
    setLoadingMore(true);
    try {
      const data = await fetchNextPage(url);
      setExtraItems((prev) => [...prev, ...(data.Items || [])]);
      setNextPageUrl(data.NextPageLink || null);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(true); }
  };

  const SortIndicator = ({ col }) =>
    sortKey === col ? (sortAsc ? ' ↑' : ' ↓') : null;

  const allItems = [...items, ...extraItems];
  const sortedItems = sortKey
    ? [...allItems].sort((a, b) => {
        const va = a[sortKey]; const vb = b[sortKey];
        return sortAsc
          ? (typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb)))
          : (typeof vb === 'number' ? vb - va : String(vb).localeCompare(String(va)));
      })
    : allItems;

  return (
    <div>
      <h2 className="text-2xl font-bold mb-4">Public IP Pricing</h2>
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <Select value={region} onValueChange={setRegion}>
          <SelectTrigger className="w-full sm:w-[200px]"><SelectValue placeholder="Select Region" /></SelectTrigger>
          <SelectContent>{regions.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={sku} onValueChange={setSku}>
          <SelectTrigger className="w-full sm:w-[200px]"><SelectValue placeholder="Select SKU" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Standard">Standard</SelectItem>
            <SelectItem value="Basic">Basic</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={handleFetch} disabled={isLoading || !region || !sku}>
          {isLoading ? 'Loading…' : 'Fetch Prices'}
        </Button>
      </div>

      {error && <div className="text-red-500 mb-4 text-sm">{error.message}</div>}
      {isLoading && <SkeletonTable rows={3} cols={3} />}

      {!isLoading && sortedItems.length > 0 && (
        <>
          <p className="text-sm text-gray-500 mb-2">Showing {sortedItems.length} result(s)</p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="cursor-pointer select-none" onClick={() => handleSort('skuName')}>SKU Name<SortIndicator col="skuName" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => handleSort('meterName')}>Meter<SortIndicator col="meterName" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => handleSort('unitPrice')}>Unit Price<SortIndicator col="unitPrice" /></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedItems.map((price) => (
                <TableRow key={`${price.skuId}-${price.meterName}`}>
                  <TableCell>{price.skuName}</TableCell>
                  <TableCell>{price.meterName}</TableCell>
                  <TableCell>{formatPrice(price.unitPrice, price.currencyCode, price.unitOfMeasure)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {(nextPageUrl || nextPageLink) && (
            <div className="mt-4 text-center">
              <Button onClick={handleLoadMore} disabled={loadingMore} className="bg-gray-100 text-gray-700 hover:bg-gray-200">
                {loadingMore ? 'Loading…' : 'Load More'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PublicIPPricing;
